import {Autocomplete, Box, FormLabel, Grid} from "@mui/material"
import {Controller, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import * as z from "zod";
import {checkQueryKey, emptyValue, getRealId, getRealSearch, getRealWrite} from "@/helper/method";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import React from "react";
import {DictPO} from "@/model/system";
import {useAllDict} from "@/store/dict.c";
import {DialogFooter} from "@/widget/DialogModal/DialogModal";
import {useCreateRecord, useQueryRecord, useUpdateRecord} from "@/service/record.s";
import {ModeType, useDialogModal, useDrawerModal, useGlobalActions} from "@/store/global.c";
import {SearchData} from "@/widget/SearchData/SearchData";
import {CustomerPO} from "@/model/customer";

const validationSchema = z.object({
    typeId: z.number({message: "请选择跟进内容"}),
    type: z.ostring().default(""),
    wayId: z.number({message: "请选择跟进方式"}),
    way: z.ostring().default(""),
    id: z.number().default(0),
    targetName: z.unknown(),
    otherId: z.unknown().default(0),
    targetId: z.number({message: "请选择客户名称"}).nullable(),
    content: z.string({message: "请填写跟进记录"}).nonempty({message: "请填写跟进记录"}),
    customerName: z.unknown(),
    // creatorId: z.number({message: "请选择跟进人"}),
    // creator: z.ostring().default(""),
    // createdAt: z.date({message: "请选择跟进时间"}),
});

export type WriteRecordForm = z.infer<typeof validationSchema>

const defaultValues = emptyValue(validationSchema)

export interface WriteRecordProps {
    targetId?: number
    mode: ModeType
}

export const RecordForm: React.FC<WriteRecordProps> = ({targetId, mode}) => {
    const {mutateAsync: createRecordMutate, isPending: createRecordPending} = useCreateRecord()
    const {mutateAsync: updateRecordMutate, isPending: updateRecordPending} = useUpdateRecord()
    const {write: dialogWrite, onSearch: dialogOnSearch, id: dialogId} = useDialogModal()
    const {write: drawerWrite, onSearch: drawerOnSearch, id: drawerId} = useDrawerModal()
    const {setDialogModal, setToast, setDrawerModal} = useGlobalActions()
    const more = !checkQueryKey(targetId)
    const write = getRealWrite(mode, drawerWrite!, dialogWrite)
    const id = getRealId(mode, drawerId, dialogId)
    const onSearch = getRealSearch(mode, drawerOnSearch, dialogOnSearch)
    const {recordType, recordWay} = useAllDict()
    const {data: recordData} = useQueryRecord({id: id}, checkQueryKey(id))
    const record = recordData?.data
    const {
        register,
        control,
        setValue,
        handleSubmit,
        reset,
        formState: {errors},
    } = useForm<WriteRecordForm>({
        resolver: zodResolver(validationSchema),
        values: {targetId, ...record, targetName: record?.customerName} as WriteRecordForm,
        defaultValues: {...defaultValues, targetId},
    });

    const onSubmit = async (values: WriteRecordForm) => {
        const result = write == 'add' ? await createRecordMutate({...values, otherId: values.targetId}) : await updateRecordMutate({...values, id: id!, otherId: values.targetId})
        if (result.code == 0) {
            onSearch && onSearch()
            setToast({open: true, message: result.msg, severity: "success", variant: 'standard'})
            mode == 'dialog' && setDialogModal({open: false})
            mode == 'drawer' && setDrawerModal({open: false})
        } else {
            setToast({open: true, message: result.msg, severity: "error", variant: 'standard'})
        }
    }

    return <Box component={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
            {more && <Grid size={12}>
                <SearchData<WriteRecordForm, CustomerPO> required={true} errMsg={errors?.targetId?.message} control={control} attr={"targetName"} attrId={"targetId"}
                                                         setValue={setValue}
                                                         label={"客户名称"}/> </Grid>}
            <Grid size={12}>
                <FormControl fullWidth={true}>
                    {more && <FormLabel>记录内容</FormLabel>}
                    <TextField
                        multiline
                        rows={3}
                        {...register("content")}
                        error={!!errors?.content}
                        helperText={errors?.content?.message}
                        slotProps={{
                            input: {
                                sx: {height: 80},
                                inputProps: {maxLength: 250},
                            },
                        }}
                        placeholder="填写记录内容"
                        fullWidth
                    />
                </FormControl>
            </Grid>
            <Grid size={6}>
                <FormControl fullWidth={true} required={true}>
                    {more && <FormLabel>跟进方式</FormLabel>}
                    <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                        options={recordWay || []}
                        value={value as any}
                        onChange={(_, option: DictPO | null) => {
                            setValue("wayId", option!.value)
                            onChange(option?.label)
                        }}
                        fullWidth
                        renderInput={(params) => (
                            <TextField slotProps={{}} error={!!errors?.wayId}
                                       helperText={errors?.wayId?.message}
                                       variant="outlined"
                                       placeholder={"选择跟进方式"} {...params} />
                        )}
                    />} name={"way"}/>
                </FormControl>
            </Grid>
            <Grid size={6}>
                <FormControl fullWidth={true} required={true}>
                    {more && <FormLabel>跟进内容</FormLabel>}
                    <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                        options={recordType || []}
                        value={value as any}
                        onChange={(_, option: DictPO | null) => {
                            setValue("typeId", option!.value)
                            onChange(option?.label)
                        }}
                        fullWidth
                        renderInput={(params) => (
                            <TextField slotProps={{}} error={!!errors?.wayId}
                                       helperText={errors?.wayId?.message}
                                       variant="outlined"
                                       placeholder={"选择跟进内容"} {...params} />
                        )}
                    />} name={"type"}/>
                </FormControl>
            </Grid>
        </Grid>
        <DialogFooter loading={createRecordPending || updateRecordPending} onReset={() => reset(defaultValues)}/>
    </Box>
}